import type { Form } from 'src/app/state-man/models/form.model'

export type FormCode = 'IST' | 'IST-PRV' | 'AVI' | 'BMS' | 'CIR' | 'CMC' | 'CME' | 'THI' | 'TIR' | 'TOF' | 'VRT' | 'VVR' | 'WSR'
export type WoArea = 'PRV' | 'WTS' | 'WTX' | 'OTHER'

export interface FormType {
  readonly code: FormCode
  readonly typeId: number          // rt2.form_types id
  readonly slug: string            // route segment: forms/{slug}-form/...
  readonly name: string            // picker and group card: "Test Only Form"
  readonly areas: readonly WoArea[]
  readonly duplicable: boolean     // Add Another / Duplicate shown on cards
}

const PRV: readonly WoArea[] = ['PRV']

/** The 13 form types. Picker, group names, routes, duplicate rules and WO-prefix availability all read this table. */
export const FORM_CATALOG = {
  'AVI': { code: 'AVI', typeId: 3, slug: 'avi', name: 'ANSI Valve Inspection Form', areas: PRV, duplicable: true },
  'BMS': { code: 'BMS', typeId: 10, slug: 'bms', name: 'Burner Management System Form', areas: PRV, duplicable: false },
  'CIR': { code: 'CIR', typeId: 12, slug: 'cir', name: 'Crane Inspection Report', areas: ['WTS'], duplicable: true },
  'CMC': { code: 'CMC', typeId: 13, slug: 'cmc', name: 'Critical Measurement — Compressor', areas: ['WTX'], duplicable: true },
  'CME': { code: 'CME', typeId: 14, slug: 'cme', name: 'Critical Measurement — Engine', areas: ['WTX'], duplicable: true },
  'IST': { code: 'IST', typeId: 1, slug: 'ist', name: 'Internal Service Ticket', areas: ['WTS', 'WTX', 'OTHER'], duplicable: false },
  'IST-PRV': { code: 'IST-PRV', typeId: 2, slug: 'ist-prv', name: 'PRV Internal Service Ticket', areas: PRV, duplicable: false },
  'THI': { code: 'THI', typeId: 6, slug: 'thi', name: 'Thief Hatch Inspection Form', areas: PRV, duplicable: true },
  'TIR': { code: 'TIR', typeId: 11, slug: 'tir', name: 'Thermographic Inspection Report', areas: PRV, duplicable: true },
  'TOF': { code: 'TOF', typeId: 5, slug: 'tof', name: 'Test Only Form', areas: PRV, duplicable: true },
  'VRT': { code: 'VRT', typeId: 4, slug: 'vrt', name: 'Valve Repair Traveler Form', areas: PRV, duplicable: false },
  'VVR': { code: 'VVR', typeId: 7, slug: 'vvr', name: 'Vent Valve Repair Form', areas: PRV, duplicable: true },
  'WSR': { code: 'WSR', typeId: 8, slug: 'wsr', name: 'Work Summary Report', areas: ['PRV', 'WTS', 'WTX', 'OTHER'], duplicable: false },
} as const satisfies { readonly [C in FormCode]: FormType & { readonly code: C } }

export const FORM_CODES = Object.keys(FORM_CATALOG) as readonly FormCode[]

const isFormCode = (s: string): s is FormCode => Object.prototype.hasOwnProperty.call(FORM_CATALOG, s)

/** 'PR…' → PRV (PRV and PRN), 'WTS…', 'WTX…', else OTHER. */
export function woArea(woNumber: string): WoArea {
  const wo = (woNumber ?? '').toUpperCase()
  if (wo.startsWith('PR')) return 'PRV'
  if (wo.startsWith('WTS')) return 'WTS'
  if (wo.startsWith('WTX')) return 'WTX'
  return 'OTHER'
}

/** Picker list for a job, in catalog order. */
export function formsFor(woNumber: string): readonly FormType[] {
  const area = woArea(woNumber)
  return FORM_CODES.map(c => FORM_CATALOG[c] as FormType).filter(t => t.areas.includes(area))
}

/** form.form_name → FormCode; null for legacy or unknown names (the item still lists under its raw name). */
export function formCodeOf(form: Pick<Form, 'form_name'>): FormCode | null {
  const name = (form.form_name ?? '').toUpperCase()
  return isFormCode(name) ? name : null
}
